import type { ProjectVersionCreatedData } from '@/services/website/socket/types';
import { useChatStore } from '@/stores/chat';
import { useProjectStore } from '@/stores/project';
import { collect } from './helper';

export type ProjectVersionCreatedDiagnosticStage =
  | 'socket_received'
  | 'socket_filtered'
  | 'state_applied'
  | 'state_rejected';

interface VersionStateSnapshot {
  lastVersionId: string;
  mappedVersionId: string | null;
}

interface TrackProjectVersionCreatedDiagnosticOptions {
  stage: ProjectVersionCreatedDiagnosticStage;
  reason?: string;
  before?: VersionStateSnapshot;
  after?: VersionStateSnapshot;
}

function getVersionStateSnapshot(messageId: string | null | undefined): VersionStateSnapshot {
  const projectState = useProjectStore.getState();
  return {
    lastVersionId: projectState.lastVersionId,
    mappedVersionId: messageId ? projectState.versions.get(messageId)?.versionId ?? null : null,
  };
}

/** 记录版本创建事件在 Socket 和前端版本状态中的处理结果。 */
export function trackProjectVersionCreatedDiagnostic(
  data: ProjectVersionCreatedData | null | undefined,
  options: TrackProjectVersionCreatedDiagnosticOptions,
): void {
  const payload = data?.data;
  const record = payload?.record;
  const eventMessageId = payload?.messageId ?? null;
  const recordMessageId = record?.messageId ?? null;
  const before = options.before ?? getVersionStateSnapshot(recordMessageId);
  const after = options.after ?? getVersionStateSnapshot(recordMessageId);
  const projectState = useProjectStore.getState();
  const chatState = useChatStore.getState();

  collect('project_version_created_diagnostic', {
    schema_version: 1,
    stage: options.stage,
    reject_reason: options.reason ?? null,
    record_project_id: record?.projectId ?? null,
    store_project_id: projectState.projectId || null,
    record_session_id: record?.sessionId ?? null,
    event_message_id: eventMessageId,
    record_message_id: recordMessageId,
    version_id: record?.id ?? null,
    commit_sha: record?.commitSha ?? null,
    record_is_ready: record?.isReady ?? null,
    record_is_visible: record?.isVisible ?? null,
    has_preview_url: Boolean(payload?.previewUrl),
    event_timestamp: data?.timestamp ?? null,
    record_created_at: record?.createdAt ?? null,
    last_version_id_before: before.lastVersionId || null,
    last_version_id_after: after.lastVersionId || null,
    mapped_version_id_before: before.mappedVersionId,
    mapped_version_id_after: after.mappedVersionId,
    is_streaming: chatState.isStreaming,
    last_output_done_message_id: chatState.lastOutputDoneMessageId,
  });
}
